/**
 * Renders a Travel Risk Brief section body. Resolver bodies (weather, routes,
 * medical, kidnapping table, …) embed GitHub-flavored markdown pipe tables;
 * without parsing them the preview shows raw `| a | b |` rows plus the
 * `| --- |` separator line. This splits the body into ordered prose + table
 * blocks and renders real tables. Mirrors the backend
 * `tripBrief/markdownBlocks.ts` parser used by the PDF / DOCX exports.
 */

type Block =
  | { type: 'text'; text: string }
  | { type: 'table'; headers: string[]; rows: string[][] };

const ROW = /^\s*\|.*\|\s*$/;

function isTableRow(line: string): boolean {
  return ROW.test(line);
}

function isSeparatorRow(line: string): boolean {
  return isTableRow(line) && /^[\s:|-]+$/.test(line.trim()) && line.includes('-');
}

function splitCells(line: string): string[] {
  return line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
}

function parseBlocks(body: string): Block[] {
  const blocks: Block[] = [];
  const lines = body.split('\n');
  let buffer: string[] = [];

  const flush = (): void => {
    const text = buffer.join('\n').replace(/^\n+|\n+$/g, '');
    if (text.trim().length > 0) {
      blocks.push({ type: 'text', text });
    }
    buffer = [];
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const startsTable =
      isTableRow(line) && !isSeparatorRow(line) && i + 1 < lines.length && isSeparatorRow(lines[i + 1]);
    if (startsTable) {
      flush();
      const headers = splitCells(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && isTableRow(lines[i]) && !isSeparatorRow(lines[i])) {
        rows.push(splitCells(lines[i]));
        i++;
      }
      i--;
      blocks.push({ type: 'table', headers, rows });
    } else {
      buffer.push(line);
    }
  }
  flush();
  return blocks;
}

const stripBold = (s: string): string => s.replace(/\*\*/g, '');

export function SectionBody({ body }: { body: string }) {
  const blocks = parseBlocks(body);
  return (
    <div className="space-y-3">
      {blocks.map((block, bi) =>
        block.type === 'table' ? (
          <div key={bi} className="overflow-x-auto">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="bg-secondary-100 dark:bg-secondary-800">
                  {block.headers.map((h, ci) => (
                    <th
                      key={ci}
                      className="border border-secondary-300 px-2 py-1 text-left font-semibold text-secondary-900 dark:border-secondary-600 dark:text-secondary-100"
                    >
                      {stripBold(h)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, ri) => (
                  <tr key={ri} className="even:bg-secondary-50 dark:even:bg-secondary-800/40">
                    {block.headers.map((_, ci) => (
                      <td
                        key={ci}
                        className="border border-secondary-200 px-2 py-1 text-secondary-800 dark:border-secondary-700 dark:text-secondary-100"
                      >
                        {stripBold(row[ci] ?? '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p
            key={bi}
            className="whitespace-pre-wrap text-sm leading-relaxed text-secondary-800 dark:text-secondary-100"
          >
            {stripBold(block.text)}
          </p>
        ),
      )}
    </div>
  );
}

export default SectionBody;
